import { BehaviorSubject, Subject } from 'rxjs';
import { ResourcesService } from '../lib/das-api/api/resources.service';
import { fromNullable, none, some } from 'fp-ts/lib/Option';
import { ContainerName } from '../lib/das-api/model/containerName';
import { ImageSize } from '../lib/das-api/model/imageSize';
import { TAnalysisStaticImage } from '../models/analysis-static-image.model';
import { WBGetStartedImages } from '../lib/das-api/model/wBGetStartedImages';
import { LeadService } from '../lib/das-api/api/lead.service';
import { AdvisorContractService } from '../lib/das-api/api/advisorContract.service';

import { TAnalysisAccountDetailResponse } from '../models/analysis-account-details.model';
import { TAdvisorContractResponse } from '../models/advisor-contract.model';
import { defaultAnalysisAccountDetails } from '../constants/data.constant';
import { ReportService } from '../lib/das-api/api/report.service';
import { StampRequest } from '../lib/das-api/model/stampRequest';
import { StampType } from '../lib/das-api/model/stampType';
import { StampLocationType } from '../lib/das-api/model/stampLocationType';
import { AppApi } from './app.api';
import { LeadApi } from './lead.api';
import { AMPError } from '@assetmark/ui-kit/dist/utils/errors';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace AnalysisApi {
	// export const goalAccountDetails$ = new BehaviorSubject<TAnalysisAccountDetailResponse>(defaultAnalysisAccountDetails);
	export const goalAccountDetails$ = new BehaviorSubject<TAnalysisAccountDetailResponse>({} as TAnalysisAccountDetailResponse);
	export const advisorContractDetails$ = new BehaviorSubject<TAdvisorContractResponse>({} as TAdvisorContractResponse);
	export const savedPdfUrl$ = new Subject<string>();
	export const errorSubj$ = new Subject<AMPError>();
	export const pdfError$ = errorSubj$.asObservable();
	
	// 6 Risk profile image
	export const getRiskToleranceImages = () => {
	    return ResourcesService.apiDasV1ResourcesImagesGet(ContainerName.Riskprofileimages, ImageSize.Medium)
            .mapRD(data => data.map(parseImage))
			.shareReplay(1);
	};

	// Static financial image
	export const getFinancialImages = () => {
	    return ResourcesService.apiDasV1ResourcesImagesGet(ContainerName.Youranalysisimages, ImageSize.Small)
            .mapRD(data => data.map(parseImage))
			.shareReplay(1);
	};

	export const getAdvisorContractDetails = ((agentId: string) => {
		LeadApi.showSpinner$.next(true);
		AdvisorContractService.apiDasV1AdvisorcontractAgentIdContactinfoGet(agentId)
		.subscribe(data => {
            if(data.isSuccess()) {
				LeadApi.showSpinner$.next(false);
				advisorContractDetails$.next(data.value);
            }
        });
    });

	export const getGoalDetailsForAnalysis = ((leadStrategyId: string, agentId: string) => {
		LeadApi.showSpinner$.next(true);
		LeadService.apiDasV1LeadLeadStrategyLeadStrategyIDAccountDetailsGet(leadStrategyId, agentId)
		.subscribe(data => {
            if(data.isSuccess()) {
				LeadApi.showSpinner$.next(false);
                goalAccountDetails$.next(data.value);
            }
        });
    });

	export const saveLeadPdf = (leadId: string, agentId: string, pages: string[]) => {
		const stampRequest: StampRequest[] = [];
		let startPageNumber = 0;
		
		pages.forEach((page, idx) => {
			startPageNumber = idx + 1;
			const stampMetadata = {
				stampType: StampType.PageNumber,
				stampLocationtype: StampLocationType.StampByCoordinate,
				stampCoordinate: {
					xAxis: 750,
					yAxis: 24,
				},
				stampFieldID: `page-${idx+1}`,
				startValue: startPageNumber,
				incrementBy: 1,
				skipFirstAmountOfPages: 0,
			};
	
			if (idx === 0) {
				stampMetadata.skipFirstAmountOfPages = 1;
			}
	
			stampRequest.push({
				document: { name: `page-${idx+1}`, content: page, insertBlankPage: true },
				stampMetadata,
			});
		});

		ReportService.apiDasV1ReportSaveleadpdfLeadIdPost(leadId, stampRequest, agentId)
		.subscribe(data => {
				if(data.isSuccess()) {
					savedPdfUrl$.next(`/api/das/v1/Resources?url=${data.value}`);
				}
			});
    };

    const parseImage = (img: WBGetStartedImages): TAnalysisStaticImage => {
		return {
			imageUri: fromNullable(img.imageUri ? `/api/das/v1/Resources?url=${img.imageUri}` : null),
			index: fromNullable(img.index),
			imageSizeType: fromNullable(img.imageSizeType),
		};
	};
}